import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { Reason, TenantTier, TenantTierEdit, TenantTierStatus } from "../admin-client";
import { useTenantTiers, useUpdateTenantTier } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { Money, StatusBadge } from "./common";
import { ASSUMED_EXPONENT, currencyExponent } from "./money-units";

/**
 * Operator tiers — the price list every tenant is sold from.
 *
 * THE POINT OF THIS SCREEN is that a tier's price and its allowance are ROWS,
 * not constants: raising Growth from 150M tokens to 200M is a form here, not a
 * deploy. That is the whole of M4's "editable rows a super admin can change
 * without a deploy", and it is why nothing about a tier is compiled into Go.
 *
 * PLATFORM-ONLY, and the API says so first: `tenants:manage` is in the Admin
 * API's platform-only permission set, so AllowedInScope refuses it to a
 * tenant-confined subject however senior — a tenant that could edit this table
 * would be setting its own allowance. This screen is the courtesy, so an
 * operator meets the rule as a sentence rather than as an audited 403.
 *
 * WHAT AN EDIT DOES NOT DO. It does not move anybody who is already on the
 * tier. The allowance a subscriber is spending was COPIED onto its usage period
 * when that period opened, so a change here is what the NEXT period opens with
 * — and archiving a tier withdraws it from sale without cancelling a single
 * live subscription. Both sentences are on the screen, because an operator
 * about to re-price a tier real customers are on should not have to guess.
 */
export function Tiers() {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, "tenants:manage") ? (
          <TierConsole />
        ) : (
          <>
            <PageHeader title="Operator tiers" />
            <Card>
              <Alert tone="info" title="This price list belongs to the platform">
                <p>
                  A tier is what the platform charges an operator company and how
                  many tokens that buys, and there is one list for everybody — so
                  only platform staff maintain it. What <em>your</em> company is
                  on, and how much of it is left, is on <strong>Billing</strong>.
                </p>
              </Alert>
            </Card>
          </>
        )
      }
    </RequireOperator>
  );
}

/** Every status a tier row can be in, said in the operator's words. A Record
 *  keyed by the union, so widening TenantTierStatus fails to typecheck until
 *  the new state is described rather than silently missing from the picker. */
const STATUS_HINTS: Record<TenantTierStatus, string> = {
  draft: "Written and not offered. Nobody can buy it.",
  active: "On sale. It appears in every tenant's upgrade picker.",
  archived:
    "Withdrawn from sale. Tenants already on it stay on it and keep their allowance — this only closes the picker.",
};

/** Tokens with separators. A tier's allowance is eight or nine digits and an
 *  unseparated one is unreadable at exactly the moment it matters: the
 *  difference between 15,000,000 and 150,000,000 is one keystroke. */
export function tokenCount(value: number): string {
  return value.toLocaleString("en-US");
}

/** How long one period of this tier lasts, said rather than spelled. `once` is
 *  not a short month: it is a grant that never expires and never rolls over,
 *  which is what makes the free tier a fixed million tokens rather than an
 *  unlimited tier on a delay. */
export function intervalLabel(tier: {
  billing_interval: string;
  interval_count: number;
}): string {
  if (tier.billing_interval === "once") {
    return "one-time grant";
  }
  return tier.interval_count === 1
    ? `per ${tier.billing_interval}`
    : `per ${tier.interval_count} ${tier.billing_interval}s`;
}

function TierConsole() {
  const tiers = useTenantTiers();
  const [editing, setEditing] = useState<TenantTier | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (tiers.isPending) {
    return <Spinner label="Loading operator tiers" />;
  }
  if (tiers.error) {
    return <ErrorAlert error={tiers.error} title="Could not load the operator tiers" />;
  }

  const rows = tiers.data?.tiers ?? [];

  return (
    <>
      <PageHeader
        title="Operator tiers"
        description="What an operator company pays the platform, and how many tokens that buys per period. Every value here is a row: changing one takes effect on the next period a tenant opens, and never on one being spent."
      />
      {notice ? (
        <Alert tone="success" title="Saved">
          <p>{notice}</p>
        </Alert>
      ) : null}
      {editing ? (
        <Card>
          <TierForm
            tier={editing}
            onClose={() => setEditing(null)}
            onDone={(message) => {
              setNotice(message);
              setEditing(null);
            }}
          />
        </Card>
      ) : null}
      <Card>
        <DataTable<TenantTier>
          rows={rows}
          rowKey={(row) => row.id}
          empty="No tier has been seeded. Until one exists, no tenant can be metered at all."
          columns={[
            {
              key: "name",
              header: "Tier",
              render: (row) => (
                <>
                  <div>{row.name}</div>
                  <code className="esm-muted-text">{row.slug}</code>
                </>
              ),
            },
            {
              key: "price",
              header: "Price",
              render: (row) => (
                <>
                  <Money value={row.price} />{" "}
                  <span className="esm-muted-text">{intervalLabel(row)}</span>
                </>
              ),
            },
            {
              key: "allowance",
              header: "Included tokens",
              align: "end",
              // A COUNT, never money. There is no exchange rate between this
              // column and the one beside it and none should be inferred.
              render: (row) => <span>{tokenCount(row.included_tokens)}</span>,
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) => (
                <div className="esm-actions esm-actions--table">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setNotice(null);
                      setEditing(row);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}

/**
 * A price box as whole minor units.
 *
 * Separate from money-units' parseUnits, which speaks micros: a tier price is a
 * CARD CHARGE in whole cents, and the API takes `price_minor` for that reason.
 * A figure with more decimals than the currency has is REFUSED rather than
 * rounded — a third of a cent charged to a card is not a thing to guess at.
 */
export function parseMinor(text: string, exponent: number): { minor: number } | { error: string } {
  const trimmed = text.trim();
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) {
    return { error: "Digits and a decimal point only — no currency symbol and no separators." };
  }
  const fraction = match[2] ?? "";
  if (fraction.length > exponent) {
    return {
      error:
        exponent === 0
          ? "This currency has no decimal places."
          : `At most ${exponent} decimal places — a tier is charged to a card in whole cents.`,
    };
  }
  const minor = Number(`${match[1]}${fraction.padEnd(exponent, "0")}`);
  if (!Number.isSafeInteger(minor)) {
    return { error: "That is larger than a price this platform can hold." };
  }
  return { minor };
}

/** The row's price as whole minor units — what `price_minor` on the wire
 *  means. Amount carries micros, and a tier is charged in cents, so the two
 *  differ by the currency's own scale and nothing else. */
function minorOf(price: { micros: number }, exponent: number): number {
  return Math.round(price.micros / 10 ** (6 - exponent));
}

/** A token allowance box: a whole non-negative count, no separators. */
function parseTokens(text: string): { tokens: number } | { error: string } {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) {
    return { error: "Whole tokens only — digits, with no separators and no decimal point." };
  }
  const tokens = Number(trimmed);
  if (!Number.isSafeInteger(tokens)) {
    return { error: "That is more tokens than this platform can count." };
  }
  return { tokens };
}

interface TierFormState {
  name: string;
  description: string;
  status: TenantTierStatus;
  price: string;
  tokens: string;
}

function TierForm({
  tier,
  onClose,
  onDone,
}: {
  tier: TenantTier;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const update = useUpdateTenantTier();
  // The row's own currency decides the scale. It is not editable here: a tier
  // re-denominated in place would leave every completed checkout priced in a
  // currency the plan no longer claims, and the honest way to sell a tier in
  // another currency is another tier.
  const exponent = currencyExponent(tier.price.currency) ?? ASSUMED_EXPONENT;
  const [form, setForm] = useState<TierFormState>(() => ({
    name: tier.name,
    description: tier.description ?? "",
    status: tier.status,
    price: (minorOf(tier.price, exponent) / 10 ** exponent).toFixed(exponent),
    tokens: String(tier.included_tokens),
  }));

  const set = (field: keyof TierFormState) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const price = parseMinor(form.price, exponent);
  const tokens = parseTokens(form.tokens);
  const priceError = "error" in price ? price.error : undefined;
  const tokensError = "error" in tokens ? tokens.error : undefined;
  const ready =
    form.name.trim() !== "" && priceError === undefined && tokensError === undefined;

  async function run(reason: Reason) {
    // PATCH semantics all the way down: only what actually changed is sent, so
    // a field the operator did not touch cannot be blamed for a difference
    // somebody else saved while this form was open.
    const body: TenantTierEdit = {};
    if (form.name.trim() !== tier.name) {
      body.name = form.name.trim();
    }
    if (form.description.trim() !== (tier.description ?? "")) {
      body.description = form.description.trim();
    }
    if (form.status !== tier.status) {
      body.status = form.status;
    }
    if ("minor" in price && price.minor !== minorOf(tier.price, exponent)) {
      body.price_minor = price.minor;
    }
    if ("tokens" in tokens && tokens.tokens !== tier.included_tokens) {
      body.included_tokens = tokens.tokens;
    }
    const result = await update.mutateAsync({ tierID: tier.id, body, reason });
    onDone(
      `${result.tier.name} is ${result.tier.status}, ${result.tier.price.currency} ${result.tier.price.amount} ${intervalLabel(result.tier)}, ${tokenCount(result.tier.included_tokens)} tokens.`,
    );
  }

  return (
    <div className="esm-subform">
      <h3>Edit {tier.name}</h3>
      <p className="esm-muted-text">
        Nobody already on this tier is moved. The allowance a company is spending
        was copied onto its period when that period opened, so a change here is
        what the <em>next</em> period opens with — and archiving withdraws the
        tier from sale without cancelling anybody.
      </p>
      <ErrorAlert error={update.error} title="The tier was not saved" />
      <div className="esm-form-grid">
        <Field
          label="Name"
          htmlFor="tier-name"
          hint="What a tenant sees in its upgrade picker. The slug is fixed — it is what the upgrade route names this tier by."
        >
          <Input
            id="tier-name"
            value={form.name}
            onChange={(event) => set("name")(event.target.value)}
          />
        </Field>
        <Field
          label="Status"
          htmlFor="tier-status"
          hint={STATUS_HINTS[form.status]}
        >
          <Select
            id="tier-status"
            value={form.status}
            onChange={(event) => set("status")(event.target.value as TenantTierStatus)}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </Select>
        </Field>
        <Field
          label={`Price (${tier.price.currency}, ${intervalLabel(tier)})`}
          htmlFor="tier-price"
          hint="A card charge in whole cents. A zero-priced tier cannot be bought self-service at all — a free grant is something the platform awards, not something a tenant can award itself."
          error={priceError}
        >
          <Input
            id="tier-price"
            value={form.price}
            onChange={(event) => set("price")(event.target.value)}
          />
        </Field>
        <Field
          label="Included tokens"
          htmlFor="tier-tokens"
          hint="Whole tokens per period. A count, not money: there is no exchange rate between this and the price and none should be inferred."
          error={tokensError}
        >
          <Input
            id="tier-tokens"
            value={form.tokens}
            onChange={(event) => set("tokens")(event.target.value)}
          />
        </Field>
      </div>
      <Field
        label="Description"
        htmlFor="tier-description"
        hint="Shown beside the tier in a tenant's upgrade picker. Empty removes it."
      >
        <Input
          id="tier-description"
          value={form.description}
          onChange={(event) => set("description")(event.target.value)}
        />
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={update.isPending}
          disabled={!ready}
          mutation={{
            title: `Save changes to ${tier.name}`,
            tone: "primary",
            description: (
              <p>
                {tier.price.currency} {form.price} {intervalLabel(tier)} for{" "}
                {"tokens" in tokens ? tokenCount(tokens.tokens) : form.tokens}{" "}
                tokens, {form.status}. Companies already on this tier keep the
                allowance their current period opened with.
              </p>
            ),
            confirmLabel: "Save changes",
            run,
          }}
        >
          Save changes
        </ReasonButton>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
